import { useState } from "react";
import type { SearchHit, Task } from "../../api.ts";
import { useTheme } from "../../glassTheme.tsx";
import { TierDot } from "../sessions/Row.tsx";
import { shortDate } from "../../theme.ts";
import { excerpt, markRanges } from "../../searchHighlight.ts";
import { Marked } from "./Marked.tsx";

type TaskHit = Extract<SearchHit, { kind: "task" }>;
type FlagHit = Extract<SearchHit, { kind: "flag" }>;
type ItemHit = Extract<SearchHit, { kind: "item" }>;

/** The same grid as the Sessions and Board rows: a 3px rail, the text, one figure at the right. */
const grid = (colors: { hair: string }): React.CSSProperties => ({
  display: "grid", gridTemplateColumns: "3px 1fr auto", gap: 12, alignItems: "center",
  padding: "12px 0", borderBottom: `1px solid ${colors.hair}`, cursor: "pointer",
});

const idStyle = (ink2: string): React.CSSProperties => ({
  fontSize: 12, color: ink2, marginRight: 8, flex: "none", fontVariantNumeric: "tabular-nums",
});

const oneLine: React.CSSProperties = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

/** "Value › Area", without the task's own title the server appends. */
const crumb = (t: Task) =>
  t.breadcrumb.endsWith(` › ${t.title}`) ? t.breadcrumb.slice(0, -(t.title.length + 3)) : t.breadcrumb;

const stateWord = (t: Task) =>
  t.state === "parked" ? "parked" : t.state === "done" ? "done" : t.state === "waiting" ? "waiting"
    : t.state === "blocked" || t.blocked ? "blocked" : null;

export function TaskHitRow({ task, tokens, idMatch, onOpen }: {
  task: TaskHit; tokens: string[]; idMatch: boolean; onOpen: () => void;
}) {
  const { colors } = useTheme();
  const tint = task.value_id ? (colors.tint as Record<string, string>)[task.value_id] : undefined;
  // The state leads: parked and done are the ones easy to miss, and a long
  // breadcrumb would push a trailing word off the end of the line.
  const meta = [stateWord(task), crumb(task)].filter(Boolean).join(" · ");

  // A match can sit in a field the row does not show (the note, the reason it
  // was parked). Then, and only then, one quiet line says where.
  const shown = idMatch || markRanges(task.title, tokens).length > 0 || markRanges(meta, tokens).length > 0;
  const snippet = shown ? null : [task.parked_because, task.next, task.note, task.why]
    .map((f) => (f ? excerpt(f, tokens) : null)).find(Boolean) ?? null;

  return (
    <div
      onClick={onOpen} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(); }}
      style={{ ...grid(colors), opacity: task.state === "done" ? 0.55 : 1 }}
    >
      <span style={{ width: 3, height: 22, borderRadius: 2, background: tint ?? colors.hair2, justifySelf: "center" }} />
      <div style={{ lineHeight: 1.3, minWidth: 0 }}>
        <div style={{ fontSize: 15, color: colors.ink, display: "flex", alignItems: "center" }}>
          <TierDot tier={task.tier} />
          <span style={idStyle(colors.ink2)}>
            {idMatch ? <Marked text={task.id} tokens={[task.id.toLowerCase()]} /> : task.id}
          </span>
          <span style={oneLine}><Marked text={task.title} tokens={tokens} /></span>
        </div>
        <div style={{ fontSize: 12.5, color: colors.ink2, marginTop: 2, ...oneLine }}>
          <Marked text={meta} tokens={tokens} />
        </div>
        {snippet && (
          <div style={{ fontSize: 12.5, color: colors.ink2, marginTop: 1, ...oneLine }}>
            <Marked text={snippet} tokens={tokens} />
          </div>
        )}
      </div>
      <div style={{ fontSize: 13, color: colors.ink2, fontVariantNumeric: "tabular-nums" }}>
        {task.due ? shortDate(task.due) : ""}
      </div>
    </div>
  );
}

/**
 * A flag has no detail sheet of its own, so a tap opens it in place: what it
 * says, how it was resolved if it was, and the tasks it names.
 */
export function FlagHitRow({ flag, tokens, idMatch, onOpenTask }: {
  flag: FlagHit; tokens: string[]; idMatch: boolean; onOpenTask: (id: string) => void;
}) {
  const { colors } = useTheme();
  const [open, setOpen] = useState(false);
  const firstLine = (flag.state === "resolved" ? flag.resolution : flag.body) ?? flag.body ?? "";
  return (
    <div style={{ borderBottom: `1px solid ${colors.hair}`, opacity: flag.state === "resolved" ? 0.55 : 1 }}>
      <div
        onClick={() => setOpen((o) => !o)} role="button" tabIndex={0} aria-expanded={open}
        onKeyDown={(e) => { if (e.key === "Enter") setOpen((o) => !o); }}
        style={{ ...grid(colors), borderBottom: "none" }}
      >
        <span style={{ width: 3, height: 22, borderRadius: 2, background: colors.hair2, justifySelf: "center" }} />
        <div style={{ lineHeight: 1.3, minWidth: 0 }}>
          <div style={{ fontSize: 15, color: colors.ink, display: "flex", alignItems: "center" }}>
            <span style={idStyle(colors.ink2)}>
              {idMatch ? <Marked text={flag.id} tokens={[flag.id.toLowerCase()]} /> : flag.id}
            </span>
            <span style={oneLine}><Marked text={flag.title} tokens={tokens} /></span>
          </div>
          {!open && firstLine && (
            <div style={{ fontSize: 12.5, color: colors.ink2, marginTop: 2, ...oneLine }}>
              <Marked text={firstLine} tokens={tokens} />
            </div>
          )}
        </div>
        <div style={{ fontSize: 13, color: colors.ink2 }}>{flag.state === "resolved" ? "resolved" : "flag"}</div>
      </div>
      {open && (
        <div style={{ padding: "0 0 12px 15px", fontSize: 13.5, lineHeight: 1.5, color: colors.ink2 }}>
          {flag.body && <p style={{ margin: "0 0 6px", whiteSpace: "pre-wrap" }}><Marked text={flag.body} tokens={tokens} /></p>}
          {flag.resolution && <p style={{ margin: "0 0 6px", whiteSpace: "pre-wrap" }}><Marked text={flag.resolution} tokens={tokens} /></p>}
          {flag.tasks.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {flag.tasks.map((id) => (
                <button key={id} onClick={() => onOpenTask(id)} style={{
                  fontFamily: "inherit", fontSize: 12.5, color: colors.ink, background: colors.accentSoft,
                  border: "none", borderRadius: 999, padding: "6px 12px", minHeight: 32, cursor: "pointer",
                }}>{id}</button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** A collection item: kept with no obligation, so it opens nothing but its home. */
export function ItemHitRow({ item, tokens, onOpen }: { item: ItemHit; tokens: string[]; onOpen: () => void }) {
  const { colors } = useTheme();
  const tint = (colors.tint as Record<string, string>)[item.value_id];
  const meta = ["Idea", item.collection_name, item.archived ? "set aside" : null].filter(Boolean).join(" · ");
  return (
    <div
      onClick={onOpen} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(); }}
      style={{ ...grid(colors), opacity: item.archived ? 0.8 : 1 }}
    >
      <span style={{ width: 3, height: 22, borderRadius: 2, background: tint ?? colors.hair2, justifySelf: "center" }} />
      <div style={{ lineHeight: 1.3, minWidth: 0 }}>
        <div style={{ fontSize: 15, color: colors.ink, ...oneLine }}><Marked text={item.text} tokens={tokens} /></div>
        <div style={{ fontSize: 12.5, color: colors.ink2, marginTop: 2, ...oneLine }}>
          <Marked text={meta} tokens={tokens} />
        </div>
      </div>
      <div />
    </div>
  );
}
